/**
 * Captures device and browser information for debugging version update issues
 */

export interface DeviceInfo {
    userAgent: string
    platform: string
    vendor: string
    language: string
    browserName: string
    browserVersion: string
    osName: string
    osVersion: string
    isMobile: boolean
}

export function captureDeviceInfo(): DeviceInfo {
    const ua = navigator.userAgent
    
    // Detect browser and version
    let browserName = 'Unknown'
    let browserVersion = ''
    if (ua.indexOf('Firefox') > -1) {
        browserName = 'Firefox'
        const match = ua.match(/Firefox(\d+)/)
        browserVersion = match ? match[1] : ''
    } else if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
        browserName = 'Safari'
        const match = ua.match(/Version\/(\d+)/)
        browserVersion = match ? match[1] : ''
    } else if (ua.indexOf('Chrome') > -1) {
        browserName = 'Chrome'
        const match = ua.match(/Chrome\/(\d+)/)
        browserVersion = match ? match[1] : ''
    } else if (ua.indexOf('Edge') > -1) {
        browserName = 'Edge'
        const match = ua.match(/Edg[e|a]\/(\d+)/)
        browserVersion = match ? match[1] : ''
    } else if (ua.indexOf('MSIE') > -1 || ua.indexOf('Trident/') > -1) {
        browserName = 'IE'
        const match = ua.match(/MSIE (\d+)/)
        browserVersion = match ? match[1] : ''
    }
    
    // Detect OS and version
    let osName = 'Unknown'
    let osVersion = ''
    if (ua.indexOf('Win') > -1) {
        osName = 'Windows'
        const match = ua.match(/Windows NT ([\d._]+)/)
        if (match) {
            const version = match[1]
            // Map Windows NT versions to common names
            if (version === '10.0') osVersion = '11/10'
            else if (version === '6.3') osVersion = '8.1'
            else if (version === '6.2') osVersion = '8'
            else osVersion = version
        }
    } else if (ua.indexOf('Mac') > -1) {
        osName = 'macOS'
        const match = ua.match(/Mac OS X ([\d_]+)/)
        if (match) {
            const version = match[1].replace(/_/g, '.')
            // Map macOS versions to common names
            osVersion = getMacOSVersionName(version)
        }
    } else if (ua.indexOf('Linux') > -1) {
        osName = 'Linux'
    } else if (ua.indexOf('Android') > -1) {
        osName = 'Android'
        const match = ua.match(/Android ([\d.]+)/)
        osVersion = match ? match[1] : ''
    } else if (ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) {
        osName = 'iOS'
        const match = ua.match(/OS ([\d_]+)/)
        osVersion = match ? match[1].replace(/_/g, '.') : ''
    } else if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
        // Modern iPad detection (where it claims to be Mac)
        osName = 'iOS'
        osVersion = 'iPadOS (Detected)'
    }
    
    // Detect mobile
    let isMobile = /iPhone|iPad|iPod|Android|mobile/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    
    return {
        userAgent: ua,
        platform: navigator.platform || 'Unknown',
        vendor: navigator.vendor || 'Unknown',
        language: navigator.language || 'Unknown',
        browserName,
        browserVersion,
        osName,
        osVersion,
        isMobile
    }
}

function getMacOSVersionName(version: string): string {
    const versionMap: Record<string, string> = {
        '10.15': 'Catalina',
        '10.16': 'Big Sur',
        '11': 'Big Sur',
        '12': 'Monterey',
        '13': 'Ventura',
        '14': 'Sonoma',
        '15': 'Sequoia'
    }
    
    for (const [key, name] of Object.entries(versionMap)) {
        if (version.startsWith(key)) {
            return name + ' (' + version + ')'
        }
    }
    
    return version
}

export function formatDeviceInfo(info: DeviceInfo): string {
    const browser = info.browserVersion ? `${info.browserName} ${info.browserVersion}` : info.browserName
    const os = info.osVersion ? `${info.osName} ${info.osVersion}` : info.osName
    return `${browser} on ${os}`
}
